import React, { useMemo } from 'react';

const DashboardTab = ({ data, t }) => {
    const fallback = t('receptionOutpatients.details.fallback.na', { defaultValue: 'N/A' });

    const visitDate = useMemo(() => {
        if (!data?.visit_date) return fallback;
        if (typeof data.visit_date === 'string') return data.visit_date.split(' ')[0];
        try {
            return new Date(data.visit_date).toISOString().split('T')[0];
        } catch {
            return fallback;
        }
    }, [data?.visit_date, fallback]);

    const assignedDoctor = useMemo(() => {
        const v = data?.vet;
        return v?.full_name || `${v?.first_name || ''} ${v?.last_name || ''}`.trim() || fallback;
    }, [data?.vet, fallback]);

    const petAgeDisplay = useMemo(() => {
        const raw = data?.pet?.age;
        if (raw === null || raw === undefined || raw === '') return fallback;

        const toYears = (n) => t('common.ageYears', { count: n, defaultValue: `${n} years` });
        const toMonths = (n) => t('common.ageMonths', { count: n, defaultValue: `${n} months` });

        if (typeof raw === 'number' && Number.isFinite(raw)) return toYears(Math.max(0, Math.round(raw)));

        const s = String(raw).trim();
        const n = parseFloat(s.replace(/[^\d.]/g, ''));
        if (Number.isNaN(n)) return s || fallback;

        const lower = s.toLowerCase();
        const hasYear = lower.includes('year') || lower.includes('years') || lower.includes('سنة') || lower.includes('سنوات');
        const hasMonth = lower.includes('month') || lower.includes('months') || lower.includes('شهر') || lower.includes('أشهر');

        if (hasYear) return toYears(Math.max(0, Math.round(n)));
        if (hasMonth) return toMonths(Math.max(0, Math.round(n)));

        // Fallback assumption: numeric age means years
        return toYears(Math.max(0, Math.round(n)));
    }, [data?.pet?.age, fallback, t]);

    const petGenderDisplay = useMemo(() => {
        const raw = data?.pet?.gender;
        if (raw === null || raw === undefined || raw === '') return fallback;
        const s = String(raw).trim();
        const k = s.toLowerCase();
        const normalized = (k === 'm' || k === 'male') ? 'male' : (k === 'f' || k === 'female') ? 'female' : null;
        if (!normalized) return s;
        return t(`completeProfile.gender.options.${normalized}`, { defaultValue: s });
    }, [data?.pet?.gender, fallback, t]);

    const petGenderIcon = useMemo(() => {
        const k = String(data?.pet?.gender || '').trim().toLowerCase();
        if (k === 'm' || k === 'male') return 'fas fa-mars';
        if (k === 'f' || k === 'female') return 'fas fa-venus';
        return 'fas fa-venus-mars';
    }, [data?.pet?.gender]);

    if (!data) return null;

    const InfoCard = ({ icon, label, value }) => (
        <div className="info-card-premium info-card-compact">
            <div className="card-icon"><i className={icon}></i></div>
            <div className="card-details">
                <label>{label}</label>
                <p>{value}</p>
            </div>
        </div>
    );

    return (
        <div className="dashboard-tab-content tab-content-inner dashboard-summary">
            <div className="dashboard-cards-grid">
                {/* Visit Information */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.sections.visitInformation', { defaultValue: 'Visit Information' })}
                </div>
                <InfoCard
                    icon="fas fa-hashtag"
                    label={t('receptionOutpatients.details.labels.serialNumber', { defaultValue: 'Serial Number' })}
                    value={data?.out_serial_num || fallback}
                />
                <InfoCard
                    icon="fas fa-calendar-check"
                    label={t('receptionOutpatients.details.labels.visitDate', { defaultValue: 'Visit Date' })}
                    value={visitDate}
                />
                <InfoCard
                    icon="fas fa-user-md"
                    label={t('receptionOutpatients.details.labels.assignedDoctor', { defaultValue: 'Assigned Doctor' })}
                    value={assignedDoctor}
                />

                {/* Pet Information */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.sections.petInformation', { defaultValue: 'Pet Information' })}
                </div>
                <InfoCard
                    icon="fas fa-paw"
                    label={t('receptionOutpatients.details.labels.petName', { defaultValue: 'Pet Name' })}
                    value={data?.pet?.name || fallback}
                />
                <InfoCard
                    icon="fas fa-dna"
                    label={t('receptionOutpatients.details.labels.species', { defaultValue: 'Species' })}
                    value={data?.pet?.species?.name || fallback}
                />
                <InfoCard
                    icon="fas fa-fingerprint"
                    label={t('receptionOutpatients.details.labels.breed', { defaultValue: 'Breed' })}
                    value={data?.pet?.breed?.name || fallback}
                />
                <InfoCard
                    icon="fas fa-birthday-cake"
                    label={t('receptionOutpatients.details.labels.age', { defaultValue: 'Age' })}
                    value={petAgeDisplay}
                />
                <InfoCard
                    icon={petGenderIcon}
                    label={t('receptionOutpatients.details.labels.gender', { defaultValue: 'Gender' })}
                    value={petGenderDisplay}
                />
                <InfoCard
                    icon="fas fa-ban"
                    label={t('receptionOutpatients.details.labels.neutered', { defaultValue: 'Neutered/Spayed' })}
                    value={data?.pet?.neutered
                        ? t('common.yes', { defaultValue: 'Yes' })
                        : t('common.no', { defaultValue: 'No' })
                    }
                />

                {/* Guardian Information */}
                <div className="dashboard-group-header">
                    {t('receptionOutpatients.details.sections.guardianInformation', { defaultValue: 'Guardian Information' })}
                </div>
                <InfoCard
                    icon="fas fa-user-tie"
                    label={t('receptionOutpatients.details.labels.petGuardian', { defaultValue: 'Pet Guardian' })}
                    value={data?.pet_owner?.full_name || fallback}
                />
                <InfoCard
                    icon="fas fa-phone-alt"
                    label={t('receptionOutpatients.details.labels.contact', { defaultValue: 'Contact Number' })}
                    value={data?.pet_owner?.phone || fallback}
                />
            </div>
        </div>
    );
};

export default DashboardTab;
